import React, { useEffect, useRef, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControlLabel,
  Link,
  MenuItem,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography
} from '@mui/material';
import { useAuth } from '../../contexts/authContext';
import { useAlert } from '../../contexts/alertContext';
import { useTitle } from '../../hooks/titleProvider';
import { hasWorkspaceAccess, Roles } from '../../helpers/access';
import userService from '../../features/user/userService';
import gameService from '../../features/game/gameService';
import venueService from '../../features/venue/venueService';
import playerService from '../../features/player/playerService';
import aiService from '../../features/ai/aiService';

const listToText = (list) => (Array.isArray(list) ? list.join('\n') : '');

const textToList = (value) =>
  Array.from(
    new Set(
      String(value || '')
        .split(/[\n,;]/)
        .map((item) => item.trim().toLowerCase())
        .filter(Boolean)
    )
  ).sort((a, b) => a.localeCompare(b));

const QUICK_GAME_STATUS_LABELS = {
  1: 'Not Started',
  2: 'In Progress',
  3: 'Complete',
  4: 'Playoff'
};

const toDateLabel = (value) => {
  if (!value) return '';

  let date;
  if (typeof value?.toDate === 'function') date = value.toDate();
  else if (typeof value?.seconds === 'number') date = new Date(value.seconds * 1000);
  else if (typeof value?._seconds === 'number') date = new Date(value._seconds * 1000);
  else date = new Date(value);

  return date instanceof Date && !Number.isNaN(date.getTime()) ? date.toLocaleString() : '';
};

const teamPlayers = (team) =>
  Array.isArray(team?.players) && team.players.length > 0 ? team.players.join(', ') : '-';

const Settings = () => {
  const { setTitle } = useTitle();
  const { currentUserProfile, currentWorkspace } = useAuth();
  const { showAlertSuccess, showAlertError } = useAlert();
  const [whitelist, setWhitelist] = useState('');
  const [blacklist, setBlacklist] = useState('');
  const [hideQuickGamesInCurrent, setHideQuickGamesInCurrent] = useState(false);
  const [portalPersonalLoginEnabled, setPortalPersonalLoginEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingQuickGames, setSavingQuickGames] = useState(false);
  const [savingPortalLogins, setSavingPortalLogins] = useState(false);

  const [activeTab, setActiveTab] = useState(0);
  const [quickGames, setQuickGames] = useState([]);
  const [quickGamesLoading, setQuickGamesLoading] = useState(false);
  const [syncingClubs, setSyncingClubs] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [syncingMembers, setSyncingMembers] = useState(null);
  const [memberJob, setMemberJob] = useState(null);
  const [memberSyncResult, setMemberSyncResult] = useState(null);
  const memberPollRef = useRef(null);

  const [workspaces, setWorkspaces] = useState([]);
  const [workspacesLoading, setWorkspacesLoading] = useState(false);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState('');
  const [aiEnabled, setAiEnabled] = useState(false);
  const [aiProvider, setAiProvider] = useState('openai');
  const [aiModel, setAiModel] = useState('');
  const [aiApiKey, setAiApiKey] = useState('');
  const [aiHasApiKey, setAiHasApiKey] = useState(false);
  const [configLoading, setConfigLoading] = useState(false);
  const [savingAi, setSavingAi] = useState(false);

  const isSuperuser = hasWorkspaceAccess(currentUserProfile, currentWorkspace, Roles.SUPERUSER);

  useEffect(() => {
    setTitle('Settings');
  }, [setTitle]);

  useEffect(() => {
    if (!isSuperuser) {
      setLoading(false);
      return;
    }

    userService
      .getRegistrationSettings()
      .then((settings) => {
        setWhitelist(listToText(settings.whitelist));
        setBlacklist(listToText(settings.blacklist));
      })
      .catch((error) => {
        showAlertError(error?.response?.data?.message || 'Unable to load registration settings.');
      })
      .finally(() => setLoading(false));

    userService
      .getSystemSettings()
      .then((settings) => {
        setHideQuickGamesInCurrent(Boolean(settings.hideQuickGamesInCurrent));
        setPortalPersonalLoginEnabled(Boolean(settings.portalPersonalLoginEnabled));
      })
      .catch((error) => {
        showAlertError(error?.response?.data?.message || 'Unable to load system settings.');
      });
  }, [isSuperuser, showAlertError]);

  const fetchQuickGames = () => {
    setQuickGamesLoading(true);
    gameService
      .getQuickGames()
      .then((games) => {
        setQuickGames(Array.isArray(games) ? games : []);
      })
      .catch((error) => {
        showAlertError(error?.response?.data?.message || 'Unable to load quick games.');
      })
      .finally(() => setQuickGamesLoading(false));
  };

  const handleTabChange = (event, newTab) => {
    setActiveTab(newTab);

    if (newTab === 1 && quickGames.length === 0) {
      fetchQuickGames();
    }

    if (newTab === 2 && workspaces.length === 0) {
      fetchWorkspaces();
    }
  };

  const fetchWorkspaces = () => {
    setWorkspacesLoading(true);
    aiService
      .getAllWorkspaces()
      .then((list) => {
        setWorkspaces(Array.isArray(list) ? list : []);
      })
      .catch((error) => {
        showAlertError(error?.response?.data?.message || 'Unable to load workspaces.');
      })
      .finally(() => setWorkspacesLoading(false));
  };

  const fetchAiConfig = (workspaceId) => {
    setConfigLoading(true);
    aiService
      .getConfig(workspaceId)
      .then((config) => {
        setAiEnabled(Boolean(config.enabled));
        setAiProvider(config.provider || 'openai');
        setAiModel(config.model || '');
        setAiApiKey('');
        setAiHasApiKey(Boolean(config.hasApiKey));
      })
      .catch((error) => {
        showAlertError(error?.response?.data?.message || 'Unable to load AI config.');
      })
      .finally(() => setConfigLoading(false));
  };

  const handleWorkspaceSelect = (event, workspace) => {
    setSelectedWorkspaceId(workspace?.id || '');
    setAiEnabled(false);
    setAiProvider('openai');
    setAiModel('');
    setAiApiKey('');
    setAiHasApiKey(false);

    if (workspace?.id) {
      fetchAiConfig(workspace.id);
    }
  };

  const handleSaveAiConfig = async () => {
    if (!selectedWorkspaceId) {
      showAlertError('Select a workspace first.');
      return;
    }

    try {
      setSavingAi(true);
      const config = await aiService.updateConfig(selectedWorkspaceId, {
        enabled: aiEnabled,
        provider: aiProvider,
        model: aiModel,
        // Omitting apiKey keeps the currently stored key server-side.
        apiKey: aiApiKey || undefined
      });

      setAiEnabled(Boolean(config.enabled));
      setAiProvider(config.provider || 'openai');
      setAiModel(config.model || '');
      setAiApiKey('');
      setAiHasApiKey(Boolean(config.hasApiKey));
      showAlertSuccess('AI config saved.');
    } catch (error) {
      showAlertError(error?.response?.data?.message || 'Unable to save AI config.');
    } finally {
      setSavingAi(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const settings = await userService.updateRegistrationSettings({
        whitelist: textToList(whitelist),
        blacklist: textToList(blacklist)
      });

      setWhitelist(listToText(settings.whitelist));
      setBlacklist(listToText(settings.blacklist));
      showAlertSuccess('Registration settings saved.');
    } catch (error) {
      showAlertError(error?.response?.data?.message || 'Unable to save registration settings.');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveQuickGames = async () => {
    try {
      setSavingQuickGames(true);
      const settings = await userService.updateSystemSettings({ hideQuickGamesInCurrent });
      setHideQuickGamesInCurrent(Boolean(settings.hideQuickGamesInCurrent));
      showAlertSuccess('Quick games settings saved.');
    } catch (error) {
      showAlertError(error?.response?.data?.message || 'Unable to save quick games settings.');
    } finally {
      setSavingQuickGames(false);
    }
  };

  const handleSavePortalLogins = async () => {
    try {
      setSavingPortalLogins(true);
      const settings = await userService.updateSystemSettings({
        portalPersonalLoginEnabled
      });
      setPortalPersonalLoginEnabled(Boolean(settings.portalPersonalLoginEnabled));
      showAlertSuccess('Portal personal PIN settings saved.');
    } catch (error) {
      showAlertError(
        error?.response?.data?.message || 'Unable to save portal personal PIN settings.'
      );
    } finally {
      setSavingPortalLogins(false);
    }
  };

  const handleSyncClubs = async () => {
    try {
      setSyncingClubs(true);
      setSyncResult(null);
      const result = await venueService.importVenues();
      setSyncResult(result);
      showAlertSuccess(`Clubs synced: ${result.created} created, ${result.updated} updated.`);
    } catch (error) {
      const message =
        error?.response?.data?.message || error?.response?.data || 'Unable to sync clubs.';
      showAlertError(message);
    } finally {
      setSyncingClubs(false);
    }
  };

  const stopMemberPolling = () => {
    if (memberPollRef.current) {
      clearTimeout(memberPollRef.current);
      memberPollRef.current = null;
    }
  };

  const finishMemberJob = (job) => {
    stopMemberPolling();
    setSyncingMembers(null);

    if (job.status === 'failed') {
      showAlertError(job.error || 'The member sync failed.');
      return;
    }

    const report = job.report || {};
    setMemberSyncResult(report);

    if (job.dryRun) {
      showAlertSuccess(
        `Preview: ${report.created} to create, ${report.updated} to update. Nothing was written.`
      );
    } else {
      showAlertSuccess(`Members synced: ${report.created} created, ${report.updated} updated.`);
    }
  };

  // The sync runs on the server and takes minutes, so the page polls the job
  // rather than holding a request open - which is what timed out before.
  const pollMemberJob = (jobId) => {
    stopMemberPolling();

    memberPollRef.current = setTimeout(async () => {
      try {
        const job = await playerService.getMemberSyncJob(jobId);
        setMemberJob(job);

        if (job.status === 'running') {
          pollMemberJob(jobId);
        } else {
          finishMemberJob(job);
        }
      } catch (error) {
        stopMemberPolling();
        setSyncingMembers(null);
        showAlertError('Lost contact with the member sync. Reload the page to see its result.');
      }
    }, 2000);
  };

  const handleSyncMembers = async (dryRun = false) => {
    try {
      setSyncingMembers(dryRun ? 'preview' : 'sync');
      setMemberSyncResult(null);
      setMemberJob(null);
      const { jobId } = await playerService.startMemberSync(dryRun);
      pollMemberJob(jobId);
    } catch (error) {
      const message =
        error?.response?.data?.message || error?.response?.data || 'Unable to start the sync.';
      showAlertError(message);
      setSyncingMembers(null);
    }
  };

  // A reload mid-sync would otherwise lose the progress and let the page offer a
  // Sync button that the server would only refuse.
  useEffect(() => {
    if (!isSuperuser) return undefined;

    let cancelled = false;

    (async () => {
      try {
        const { job } = await playerService.getLatestMemberSync();
        if (cancelled || !job) return;

        setMemberJob(job);

        if (job.status === 'running') {
          setSyncingMembers(job.dryRun ? 'preview' : 'sync');
          pollMemberJob(job.id);
        } else if (job.status === 'complete') {
          setMemberSyncResult(job.report || {});
        }
      } catch (error) {
        // Not worth an alert on page load - the card simply shows nothing.
      }
    })();

    return () => {
      cancelled = true;
      stopMemberPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperuser]);

  if (!isSuperuser) {
    return (
      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6">Settings</Typography>
          <Typography variant="body2" color="text.secondary">
            You do not have access to system settings.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Stack spacing={2}>
      <Tabs value={activeTab} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="General" />
        <Tab label="Quick Games" />
        <Tab label="AI" />
      </Tabs>

      {activeTab === 0 && (
        <>
          <Card variant="outlined">
            <CardContent>
              <Stack spacing={1.5}>
                <Typography variant="h6">Registration Access</Typography>
                <Typography variant="body2" color="text.secondary">
                  Manage exact email addresses that can or cannot register. Blacklist entries
                  always block registration. If the whitelist has entries, only those emails can
                  register.
                </Typography>

                <TextField
                  label="Whitelist emails"
                  value={whitelist}
                  onChange={(event) => setWhitelist(event.target.value)}
                  multiline
                  minRows={5}
                  fullWidth
                  disabled={loading || saving}
                  helperText="One email per line, or separate with commas. Leave empty to allow all non-blacklisted emails."
                />

                <TextField
                  label="Blacklist emails"
                  value={blacklist}
                  onChange={(event) => setBlacklist(event.target.value)}
                  multiline
                  minRows={5}
                  fullWidth
                  disabled={loading || saving}
                  helperText="These emails cannot register, even if they also appear in the whitelist."
                />

                <Box>
                  <Button variant="contained" onClick={handleSave} disabled={loading || saving}>
                    {saving ? 'Saving...' : 'Save Registration Settings'}
                  </Button>
                </Box>
              </Stack>
            </CardContent>
          </Card>

          <Card variant="outlined">
            <CardContent>
              <Stack spacing={1.5}>
                <Typography variant="h6">Quick Games</Typography>
                <Typography variant="body2" color="text.secondary">
                  Control whether standalone quick games appear in the public current games feed
                  (GET /api/game/current).
                </Typography>

                <FormControlLabel
                  control={
                    <Switch
                      checked={hideQuickGamesInCurrent}
                      onChange={(event) => setHideQuickGamesInCurrent(event.target.checked)}
                      disabled={loading || savingQuickGames}
                    />
                  }
                  label="Hide quick games from the current games feed"
                />

                <Box>
                  <Button
                    variant="contained"
                    onClick={handleSaveQuickGames}
                    disabled={loading || savingQuickGames}>
                    {savingQuickGames ? 'Saving...' : 'Save Quick Games Settings'}
                  </Button>
                </Box>
              </Stack>
            </CardContent>
          </Card>

          <Card variant="outlined">
            <CardContent>
              <Stack spacing={1.5}>
                <Typography variant="h6">Portal Personal PINs</Typography>
                <Typography variant="body2" color="text.secondary">
                  When enabled, sending a portal link by email or WhatsApp issues each saved
                  contact their own 5-digit PIN. The shared portal PIN stays available as a
                  fallback. Turning this off stops new personal PINs; PINs already issued keep
                  working.
                </Typography>

                <FormControlLabel
                  control={
                    <Switch
                      checked={portalPersonalLoginEnabled}
                      onChange={(event) => setPortalPersonalLoginEnabled(event.target.checked)}
                      disabled={loading || savingPortalLogins}
                    />
                  }
                  label="Issue personal portal PINs when a link is sent"
                />

                <Box>
                  <Button
                    variant="contained"
                    onClick={handleSavePortalLogins}
                    disabled={loading || savingPortalLogins}>
                    {savingPortalLogins ? 'Saving...' : 'Save Portal PIN Settings'}
                  </Button>
                </Box>
              </Stack>
            </CardContent>
          </Card>

          <Card variant="outlined">
            <CardContent>
              <Stack spacing={1.5}>
                <Typography variant="h6">Bowls SA Clubs</Typography>
                <Typography variant="body2" color="text.secondary">
                  Sync clubs from the Bowls SA database (Knack) into venues. Existing venues are
                  updated and new ones created, matched by club ID.
                </Typography>
                <Box>
                  <Button variant="contained" onClick={handleSyncClubs} disabled={syncingClubs}>
                    {syncingClubs ? 'Syncing…' : 'Sync Clubs'}
                  </Button>
                </Box>
                {syncResult ? (
                  <Typography variant="body2" color="text.secondary">
                    Synced {syncResult.total} clubs — {syncResult.created} created,{' '}
                    {syncResult.updated} updated
                    {syncResult.skipped ? `, ${syncResult.skipped} skipped` : ''}.
                  </Typography>
                ) : null}
              </Stack>
            </CardContent>
          </Card>

          <Card variant="outlined">
            <CardContent>
              <Stack spacing={1.5}>
                <Typography variant="h6">Bowls SA Members</Typography>
                <Typography variant="body2" color="text.secondary">
                  Import the Bowls SA member register (Knack) into the players directory. Members
                  already here are matched by BSA number, then by full name, and get their BSA
                  number, BSA id, gender, club and district filled in wherever those are blank —
                  nothing already filled in is overwritten. Members with no record here are added.
                  It runs in the background, so this card keeps showing progress until it finishes.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    onClick={() => handleSyncMembers(false)}
                    disabled={Boolean(syncingMembers)}
                  >
                    {syncingMembers === 'sync' ? 'Syncing…' : 'Sync Members'}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => handleSyncMembers(true)}
                    disabled={Boolean(syncingMembers)}
                  >
                    {syncingMembers === 'preview' ? 'Previewing…' : 'Preview'}
                  </Button>
                </Box>
                {memberJob && memberJob.status === 'running' ? (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={16} />
                    <Typography variant="body2" color="text.secondary">
                      {memberJob.stage === 'fetching'
                        ? memberJob.totalPages
                          ? `Fetching members from Bowls SA — page ${memberJob.page} of ${memberJob.totalPages} (${memberJob.fetched.toLocaleString()} records)…`
                          : 'Fetching members from Bowls SA…'
                        : `Reconciling ${memberJob.processed.toLocaleString()} of ${memberJob.total.toLocaleString()} members — ${(
                            memberJob.counts?.created || 0
                          ).toLocaleString()} created, ${(
                            memberJob.counts?.updated || 0
                          ).toLocaleString()} updated…`}
                    </Typography>
                  </Stack>
                ) : null}
                {memberJob && memberJob.status === 'failed' ? (
                  <Typography variant="body2" color="error">
                    {memberJob.error || 'The member sync failed.'}
                  </Typography>
                ) : null}
                {memberSyncResult ? (
                  <Stack spacing={0.5}>
                    <Typography variant="body2" color="text.secondary">
                      {memberSyncResult.dryRun ? 'Preview — nothing was written. ' : ''}
                      {memberSyncResult.total} members — {memberSyncResult.created} created,{' '}
                      {memberSyncResult.updated} updated, {memberSyncResult.unchanged} already up to
                      date
                      {memberSyncResult.conflicts
                        ? `, ${memberSyncResult.conflicts} with a different BSA number`
                        : ''}
                      {memberSyncResult.ambiguous
                        ? `, ${memberSyncResult.ambiguous} ambiguous`
                        : ''}
                      {memberSyncResult.skipped ? `, ${memberSyncResult.skipped} skipped` : ''}.
                    </Typography>
                    {memberSyncResult.samples?.conflicts?.length ? (
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          These players already hold a different BSA number, so they were left
                          untouched:
                        </Typography>
                        {memberSyncResult.samples.conflicts.slice(0, 5).map((conflict) => (
                          <Typography
                            key={conflict.id}
                            variant="caption"
                            color="text.secondary"
                            display="block"
                          >
                            {`${conflict.name} ${conflict.surname}`.trim()} — on file{' '}
                            {conflict.directoryBsaNumber}, register {conflict.registerBsaNumber}
                          </Typography>
                        ))}
                      </Box>
                    ) : null}
                    {memberSyncResult.samples?.ambiguous?.length ? (
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          These members matched more than one player, so nothing was written for
                          them:
                        </Typography>
                        {memberSyncResult.samples.ambiguous.slice(0, 5).map((ambiguous, index) => (
                          <Typography
                            key={`${ambiguous.name}-${ambiguous.bsaNumber}-${index}`}
                            variant="caption"
                            color="text.secondary"
                            display="block"
                          >
                            {`${ambiguous.name} ${ambiguous.surname}`.trim() ||
                              `BSA ${ambiguous.bsaNumber}`}{' '}
                            — {ambiguous.matches} directory records match
                          </Typography>
                        ))}
                      </Box>
                    ) : null}
                  </Stack>
                ) : null}
              </Stack>
            </CardContent>
          </Card>
        </>
      )}

      {activeTab === 1 && (
        <Card variant="outlined">
          <CardContent>
            <Stack spacing={1.5}>
              <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
                <Typography variant="h6">All Quick Games</Typography>
                <Button size="small" variant="outlined" onClick={fetchQuickGames} disabled={quickGamesLoading}>
                  Refresh
                </Button>
              </Box>

              {quickGamesLoading ? (
                <Box display={'flex'} justifyContent={'center'} p={3}>
                  <CircularProgress size={24} />
                </Box>
              ) : quickGames.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No quick games created yet.
                </Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Team A</TableCell>
                        <TableCell>Team B</TableCell>
                        <TableCell>BSA Numbers</TableCell>
                        <TableCell>Created</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {quickGames.map((game) => (
                        <TableRow key={game.id}>
                          <TableCell>
                            <Link
                              href={`/public/scoring-game/${game.id}`}
                              target="_blank"
                              rel="noreferrer"
                              underline="hover">
                              {game.name || '-'}
                            </Link>
                          </TableCell>
                          <TableCell>
                            {QUICK_GAME_STATUS_LABELS[game.status] || game.status}
                          </TableCell>
                          <TableCell>{teamPlayers(game.teams?.teamA)}</TableCell>
                          <TableCell>{teamPlayers(game.teams?.teamB)}</TableCell>
                          <TableCell>
                            {Array.isArray(game.bsaNumbers) ? game.bsaNumbers.join(', ') : '-'}
                          </TableCell>
                          <TableCell>{toDateLabel(game.createdAt)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Stack>
          </CardContent>
        </Card>
      )}

      {activeTab === 2 && (
        <Card variant="outlined">
          <CardContent>
            <Stack spacing={1.5}>
              <Typography variant="h6">AI Assistant (workspace addon)</Typography>
              <Typography variant="body2" color="text.secondary">
                Enable the AI assistant for a workspace and configure which LLM provider it uses.
              </Typography>

              <Autocomplete
                options={workspaces}
                getOptionLabel={(option) => option?.name || ''}
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                value={workspaces.find((workspace) => workspace.id === selectedWorkspaceId) || null}
                onChange={handleWorkspaceSelect}
                loading={workspacesLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Workspace"
                    helperText="Select a workspace to view and edit its AI configuration."
                  />
                )}
              />

              {selectedWorkspaceId ? (
                configLoading ? (
                  <Box display={'flex'} justifyContent={'center'} p={3}>
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  <>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={aiEnabled}
                          onChange={(event) => setAiEnabled(event.target.checked)}
                          disabled={savingAi}
                        />
                      }
                      label="Enable AI assistant"
                    />

                    <TextField
                      select
                      label="Provider"
                      value={aiProvider}
                      onChange={(event) => setAiProvider(event.target.value)}
                      fullWidth
                      disabled={savingAi}>
                      <MenuItem value="openai">OpenAI</MenuItem>
                      <MenuItem value="anthropic">Anthropic</MenuItem>
                      <MenuItem value="deepseek">DeepSeek</MenuItem>
                    </TextField>

                    <TextField
                      label="Model"
                      value={aiModel}
                      onChange={(event) => setAiModel(event.target.value)}
                      fullWidth
                      disabled={savingAi}
                      helperText="Leave blank to use the provider's default model."
                    />

                    <TextField
                      label="API key"
                      type="password"
                      value={aiApiKey}
                      onChange={(event) => setAiApiKey(event.target.value)}
                      fullWidth
                      disabled={savingAi}
                      placeholder={aiHasApiKey ? '•••• (unchanged)' : ''}
                      helperText={
                        aiHasApiKey
                          ? 'A key is stored for this workspace. Leave blank to keep the current key.'
                          : 'Leave blank to keep the current key if one is already set.'
                      }
                    />

                    <Box>
                      <Button
                        variant="contained"
                        onClick={handleSaveAiConfig}
                        disabled={savingAi}>
                        {savingAi ? 'Saving...' : 'Save AI Config'}
                      </Button>
                    </Box>
                  </>
                )
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Select a workspace to view its AI configuration.
                </Typography>
              )}
            </Stack>
          </CardContent>
        </Card>
      )}
    </Stack>
  );
};

export default Settings;
